<template>
  <div class="app-page__container">
    <div v-if="!isLoading">
      <div class="users__title__container">
        <h5 class="title is-5">
          {{ `${activeWorkspace.workspaceName} Users`}}
        </h5>
        <div class="users__title__end">
          <BButton
            v-if="isLoggedInUserWorkspaceOwner"
            type="is-info"
            icon-left="plus"
            size="is-small"
            @click.prevent="handleClickInviteUser"
          >
            Invite User
          </BButton>
        </div>
      </div>
      <div class="card">
        <div class="card-content">
          <div
            v-for="user in Object.values(users)"
            :key="user.id"
            :class="[
              'users__user-details__container',
              { 'users__user-details__container--clickable': user.isActive }
            ]"
            @click.prevent="handleClickUserProfile(user.id)"
            @keyup.enter="handleClickUserProfile(user.id)"
          >
            <div style="display: flex;">
              <UserAvatar :user="user" class="u-m-r-small" />
              <div>
                <span>
                  <UserDisplayName :activeUserId="user.id" size="medium" is-inline :is-clickable="user.isActive" />
                  <TeamTag v-for="team in getUserTeams(user.id, teams)" :key="team.id" :teamId="team.id" />
                  <small v-if="!user.isActive" class="has-text-danger is-size-7 is-italic">
                    User Archived
                  </small>
                </span>
                <p class="subtitle is-size-6 is-size-7-mobile has-text-grey">
                  {{ user.email }}
                </p>
              </div>
            </div>
          </div>
          <div
            v-for="invitation in pendingInvitations"
            :key="invitation.id"
            class="users__user-details__container"
          >
            <div style="display: flex;">
              <UserAvatar :user="{}" class="u-m-r-small" />
              <div>
                <span>
                  <h6 class="title is-6 is-inline">{{ invitation.inviteeEmail }}</h6>
                  &nbsp;
                  <TeamTag :key="invitation.teamId" :teamId="invitation.teamId" />
                </span>
                <p class="subtitle is-size-6 is-size-7-mobile">
                  <span class="has-text-grey">
                    Invited on: {{ invitation.createdAt.toDate().toLocaleString('en-US') }}
                  </span>
                  <span class="has-text-info is-italic is-size-7">
                    Invitation Pending
                  </span>
                  &nbsp;
                </p>
              </div>
            </div>
            <div class="users__invitation__actions">
              <BButton
                type="is-info"
                size="is-small"
                outlined
                @click="handleResendInvitation(invitation)"
                @keyup.enter="handleResendInvitation(invitation)"
                :loading="invitationIdsResendingInProgress.includes(invitation.id)"
                :disabled="
                  invitationIdsResendingInProgress.includes(invitation.id)
                  || invitationIdsDeletingInProgress.includes(invitation.id)
                "
              >
                Resend
              </BButton>
              <BButton
                type="is-text"
                size="is-small"
                class="has-text-danger users__invitation__cancel-action"
                @click="handleDeleteInvitation(invitation)"
                @keyup.enter="handleDeleteInvitation(invitation)"
                :loading="invitationIdsDeletingInProgress.includes(invitation.id)"
              >
                Delete
              </BButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <BLoading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></BLoading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as UserActions from '@/store/actions/User.actions';
import * as TeamActions from '@/store/actions/Team.actions';
import * as InvitationActions from '@/store/actions/Invitation.actions';
import * as NotificationService from '@/services/Notification.service';
import * as NotificationTypes from '@/constants/notificationTypes';
import { getUserTeams } from '@/helpers/teamHelpers';
import { invitationStatusIds } from '@/constants/Invitation.constants';
import UserDisplayName from '@/components/UserDisplayName.vue';
import TeamTag from '@/components/TeamTag.vue';
import UserAvatar from '@/components/UserAvatar.vue';

export default {
  name: 'UsersView',

  components: {
    UserDisplayName,
    TeamTag,
    UserAvatar,
  },

  data() {
    return {
      areWorkspaceUsersLoading: true,
      areWorkspaceTeamsLoading: true,
      areWorkspaceInvitationsLoading: true,
      invitationIdsResendingInProgress: [],
      invitationIdsDeletingInProgress: [],
    };
  },

  computed: {
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
      users: state => state.user.users,
      teams: state => state.team.teams,
    }),
    ...mapGetters([
      'activeWorkspace',
      'loggedInUser',
      'isLoggedInUserWorkspaceOwner',
      'pendingInvitations',
    ]),
    isLoading() {
      return this.areWorkspaceUsersLoading || this.areWorkspaceTeamsLoading;
    },
  },

  created() {
    this.fetchInitialData();
  },

  methods: {
    getUserTeams,
    ...mapActions({
      fetchWorkspaceUsers: UserActions.FETCH_WORKSPACE_USERS,
      fetchPublicWorkspaceTeams: TeamActions.FETCH_PUBLIC_WORKSPACE_TEAMS,
      fetchUserTeams: TeamActions.FETCH_USER_TEAMS,
      fetchPendingWorkspaceInvitations: InvitationActions.FETCH_PENDING_WORKSPACE_INVITATIONS,
      voidInvitationById: InvitationActions.VOID_INVITATION_BY_ID,
      createInvitation: InvitationActions.CREATE_INVITATION,
    }),
    handleClickInviteUser() {
      this.$router.push('/invite-user');
    },
    handleClickUserProfile(userId) {
      if (!this.users[userId].isActive) return;

      this.$router.push(`/users/edit-profile/${userId}`);
    },
    async handleResendInvitation(invitation) {
      try {
        this.invitationIdsResendingInProgress.push(invitation.id);

        const newInvitationPayload = {
          inviteeEmail: invitation.inviteeEmail,
          workspaceId: this.activeWorkspace.id,
          workspaceName: this.activeWorkspace.workspaceName,
          statusId: invitationStatusIds.PENDING,
          inviterId: this.loggedInUser.id,
          inviterFirstName: this.loggedInUser.firstName,
          inviterLastName: this.loggedInUser.lastName,
          teamId: invitation.teamId,
          teamName: invitation.teamName,
        };

        await this.voidInvitationById({ invitationId: invitation.id });
        await this.createInvitation({ newInvitation: { ...newInvitationPayload } });

        this.invitationIdsResendingInProgress = this.invitationIdsResendingInProgress
          .filter(id => id !== invitation.id);

        NotificationService.showNotification(NotificationTypes.RESEND_INVITATION_SUCCESS(invitation.inviteeEmail));
      } catch (err) {
        this.invitationIdsResendingInProgress = this.invitationIdsResendingInProgress
          .filter(id => id !== invitation.id);

        NotificationService.showNotification(NotificationTypes.RESEND_INVITATION_ERROR(invitation.inviteeEmail));
      }
    },
    async handleDeleteInvitation(invitation) {
      try {
        this.invitationIdsDeletingInProgress.push(invitation.id);

        await this.voidInvitationById({ invitationId: invitation.id });

        this.invitationIdsDeletingInProgress = this.invitationIdsDeletingInProgress
          .filter(id => id !== invitation.id);

        NotificationService.showNotification(NotificationTypes.DELETE_INVITATION_SUCCESS(invitation.inviteeEmail));
      } catch (err) {
        this.invitationIdsDeletingInProgress = this.invitationIdsDeletingInProgress
          .filter(id => id !== invitation.id);

        NotificationService.showNotification(NotificationTypes.DELETE_INVITATION_ERROR(invitation.inviteeEmail));
      }
    },
    async fetchInitialData() {
      try {
        await Promise.all([
          this.fetchWorkspaceUsers({ workspaceId: this.activeWorkspaceId }),
          this.fetchPublicWorkspaceTeams({ workspaceId: this.activeWorkspaceId }),
          this.fetchUserTeams({ userId: this.loggedInUser.id, workspaceId: this.activeWorkspace.id }),
          this.fetchPendingWorkspaceInvitations({ workspaceId: this.activeWorkspaceId }),
        ]);

        this.areWorkspaceUsersLoading = false;
        this.areWorkspaceTeamsLoading = false;
        this.areWorkspaceInvitationsLoading = false;
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
  },
};
</script>

<style scoped lang="scss">
.users__title__container {
  display: flex;
  justify-content: space-between;
}

.users__title__end {
  align-items: flex-end;
}

.users__user-details__container {
  display: flex;
  flex-wrap: wrap;
  padding: $global-whitespace-small;

  &:hover {
    background-color: $white-bis;
  }
}

.users__user-details__container--clickable {
  cursor: pointer;

  &:hover {
    border-left: 2px solid $info;
    margin-left: -2px;
  }
}

.users__invitation__actions {
  margin-left: auto;
  align-self: center;
}

.users__invitation__cancel-action {
  text-decoration: none;
  margin-left: $global-whitespace-xsmall;
  color: $danger;

  :hover {
    color: darken($danger, 15%);
  }
}
</style>
